import { getLoginUrl, getEnv, isProdEnv } from '@utils/index';
import { getToken, isCookieAuthEnabled, isMobileCookieTokenAuthEnabled } from '@utils/loginHelper';
import axios from 'axios';
import { message } from '@duxton-web/ui';
import jsBridge from '@utils/jsBridge';
import { datadogRum } from '@datadog/browser-rum';
import { LANG_STORAGE } from 'src/constants/localStorage';
import { t } from 'i18next';
import { getResourceKeys, isSelectedMerchantVE, isUserPermissionSystemActive } from '@mex/mex-permissions-lib';

// Base URL configurations for different services
const BASE_URLS = {
  crm: {
    staging: 'https://api2.stg-myteksi.com/mex-app/troy/crm',
    production: 'https://api2.grab.com/mex-app/troy/crm',
  },
  merchant: {
    staging: 'https://merchant.stg-myteksi.com',
    production: 'https://merchant.grab.com',
  },
} as const;

export type ServiceType = keyof typeof BASE_URLS;

export const getBaseURL = (service: ServiceType = 'crm') => {
  const env = isProdEnv() ? 'production' : 'staging';
  return BASE_URLS[service][env];
};

const instance = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    lang: window.localStorage.getItem(LANG_STORAGE) || 'EN',
    'X-Client-ID': 'GrabMerchant-Portal',
    'X-GrabKit-ClientID': 'GrabMerchant-Portal',
  },
});

instance.interceptors.request.use(
  (config) => {
    const token = getToken();
    const shouldUseCookieAuth = isCookieAuthEnabled() || isMobileCookieTokenAuthEnabled();
    const { headers } = config;

    if (headers && token && !shouldUseCookieAuth) {
      headers['x-mts-ssid'] = token;
    }

    if (headers && (isUserPermissionSystemActive() || isSelectedMerchantVE())) {
      const resourceKeys = getResourceKeys();
      if (resourceKeys) {
        headers['x-mex-resource'] = resourceKeys;
        headers['x-mex-version'] = 'v2';
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;
      switch (status) {
        case 401:
          if (getEnv() !== 'dev') {
            const loginUrl = getLoginUrl();
            // make no sense to redirect to login page in the webview since it not a ideal webview experience
            // we may need to monitor this case
            if (jsBridge.canUseBridge()) {
              datadogRum.addAction('Redirect to login', {
                reason: '401 Unauthorized',
                hasJWT: !!getToken(),
              });
              break;
            }
            window.location.href = loginUrl;
          }
          break;
        case 403:
          message.error({
            content: 'No Permission',
            // add a key in case the error msg is shown multiple times
            key: 'error-no-permission',
          });
          break;
        default:
          message.error({
            content: error.response.data?.message || t('heading_something_went_wrong', 'Something went wrong'),
            key: 'errror-response',
          });
          break;
      }
    } else {
      message.error({
        content: t('heading_something_went_wrong', 'Something went wrong'),
        key: 'error-response',
      });
    }
    return Promise.reject(error);
  },
);

export default instance;
